import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import styles from '../Lists.module.css';
import { ListCommand } from '../../../types/ListCommands';
import StackView from '../listengine/StackEngine';

const STACK_CODE = `class Stack {
  constructor() {
    this.items = [];
  }

  push(element) {
    this.items.push(element);
  }

  pop() {
    if (this.items.length === 0)
      return "Underflow";
    return this.items.pop();
  }
}`;

const STACK_SIZE = 10;
const StackVisualizer: React.FC = () => {
  const navigate = useNavigate();
  const [command, setCommand] = useState<ListCommand | null>(null);
  const [isAnimating, setIsAnimating] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [activeLine, setActiveLine] = useState<number | null>(null);
  const animationResolver: React.MutableRefObject<((value?: void) => void) | null> = useRef(null);

  const handlePush = () => {
    if (isAnimating || inputValue === '') return;
    setIsAnimating(true);
    setActiveLine(6); 
    setCommand({
      id: Date.now(),
      action: 'addLast',
      value: parseInt(inputValue),
    });
    setInputValue('');
  };

  const handlePop = () => {
    if (isAnimating) return;
    setIsAnimating(true);
    setActiveLine(11);
    setCommand({ id: Date.now(), action: 'removeLast' }); 
  };
  
  const handleAnimationComplete = () => {
    if (animationResolver.current) {
      animationResolver.current();
      animationResolver.current = null;
    } else {
      setIsAnimating(false);
    }
    setActiveLine(null);
  };

   const handleRandomAction = async () => {
    if (isAnimating) return;
    setIsAnimating(true);

    const isAdding = Math.random() < 0.5; 
    const count = Math.floor(Math.random() * 3) + 2; 
    for (let i = 0; i < count; i++) {
      await new Promise(resolve => {
        animationResolver.current = resolve;      
        if (isAdding) {
          setActiveLine(6);
          const randomValue = Math.floor(Math.random() * 100);
          setCommand({ id: Date.now(), action: 'addLast', value: randomValue });
        } else {
          setActiveLine(11);
          setCommand({ id: Date.now(), action: 'removeLast' });
        }
      });
    }    
    setIsAnimating(false);
  };

    const handleReset = () => {
    if (isAnimating) return;
    setIsAnimating(true);
    setCommand({ id: Date.now(), action: 'reset' });
  };

  return (
    <div className={styles.container}>
      <button onClick={() => navigate(-1)} className={styles.returnButton}>
        ← Return
      </button>
      <h1 className={styles.title}>Stack(Last-in, First-Out)</h1>
      
        <div className={styles.description}>
            <h2>Description</h2>
            <p>
              A Stack follows a <strong>Last-In, First-Out (LIFO)</strong> principle, like a stack of plates. Items are added to the top (push) and removed from the top (pop).
            </p>
        </div>
        <div className={styles.useCase}>
            <h2>Use Cases</h2>
            <p>
              Used for the "Undo" feature in editors, the browser's "Back" button, and managing function calls in programming (the "call stack").
            </p>
        </div>
        <div className={styles.timeComplexity}>
            <h2>Time Complexity</h2>
            <p>
              Adding (push) and removing (pop) are always extremely fast <strong>O(1)</strong> operations. Searching for an item in the middle of the stack is slow at <strong>O(n)</strong>.
            </p>
        </div>

      <div className={styles.mainContent}>
        <div style={{flex: 1}}>
          <StackView command={command} onComplete={handleAnimationComplete} />
        </div>
        
        <div className={styles.controlsAndCode}>
            <div className={styles.controls}>
                <input
                  type="number"
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  placeholder="Value"
                  disabled={isAnimating}
                />
                <button onClick={handlePush} disabled={isAnimating }>Add</button>
                <button onClick={handlePop} disabled={isAnimating}>Remove</button>
                <button onClick={handleRandomAction} disabled={isAnimating}>Random</button> 
                <button onClick={handleReset} disabled={isAnimating}>Reset</button>

            </div>
            <div className={styles.codeDisplay}>
                {STACK_CODE.split('\n').map((line, index) => (
                <div 
                    key={index} 
                    className={`${styles.codeLine} ${activeLine === index ? styles.highlightedCode : ''}`}
                >
                    {line || ' '}
                </div>
                ))}
            </div>
        </div>
      </div>
    </div>
  );
};

export default StackVisualizer;